import { batteryConcertAmountErrorMessage } from "~/api/battery-financing-errors"
import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/battery-pricing-errors"
import type {
  BatteryProposalFinancingDto,
  BatteryQuoteDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryConcertOffers from "~/api/hooks/useBatteryConcertOffers"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useSaveBatteryConcertOffer from "~/api/hooks/useSaveBatteryConcertOffer"

import type { ConcertOfferPickerBundle } from "../../ProposalSetup/financing/concert/concert-kind-bundle"
import { ConcertOfferPicker as SharedConcertOfferPicker } from "../../ProposalSetup/financing/concert/ConcertOfferPicker"
import { REFRESH_PRICING_ERROR_PROMPT } from "../pricing-out-of-date"
import { concertAmountBlockedPrompt } from "./concert-amount-copy"

export {
  CONCERT_CREDIT_QUALITY_LABEL,
  CONCERT_OFFER_REQUIRED_PROMPT,
  CONCERT_OFFERS_LOAD_ERROR_PROMPT,
  CONCERT_PAYMENT_OUT_OF_DATE_LABEL,
  CONCERT_PROVIDER_FAILED_PROMPT,
  CONCERT_QUALITY_EMPTY_PROMPT,
  CONCERT_TERMS_LABEL,
  matchConcertOffer,
} from "../../ProposalSetup/financing/concert/ConcertOfferPicker"

export const SAVE_CONCERT_OFFER_LABEL = `Save & Refresh Pricing`
export const SAVE_CONCERT_OFFER_BUSY_LABEL = `Saving & refreshing`
export const SAVED_CONCERT_OFFER_LABEL = `Loan Product saved and payment quoted`
export const SAVED_CONCERT_OFFER_WITHOUT_PAYMENT_LABEL = `Loan Product saved. Refresh Pricing to quote its payment.`
export const CONCERT_PRICING_REQUIRED_PROMPT = `Refresh Pricing before choosing a Concert offer. Offers are only shown for the current Battery price.`
export const CONCERT_UNAVAILABLE_PROMPT = `Concert offers are not available for this Battery-Only Proposal.`
export const CONCERT_EMPTY_PROMPT = `Concert returned no Battery offers for this price.`
export const CONCERT_SELECTED_OFFER_UNAVAILABLE_PROMPT = `Concert no longer returns the saved offer. Choose another to quote a payment.`
export const CONCERT_AMOUNT_INELIGIBLE_FALLBACK_PROMPT = `This proposal's price is outside the Concert Battery amount range.`
export const SAVE_CONCERT_OFFER_ERROR_PROMPT = `Unable to save this offer. The saved Quote stays out of date until a save or refresh succeeds.`
export const CONCERT_READ_ONLY_PROMPT = `You cannot change the Loan Product for this Battery-Only Proposal.`

/**
 * Battery-Only Proposal reads and commands for the shared Concert offer
 * picker. Saving an offer reprices, so the save answers with the financing
 * read and its Quote.
 */
const batteryConcertOfferBundle: ConcertOfferPickerBundle<BatteryProposalFinancingDto> =
  {
    useOffers: useBatteryConcertOffers,
    useSave: useSaveBatteryConcertOffer,
    savedOutcome: (financing) =>
      financing.quote?.status === `current` &&
      financing.quote.estimatedMonthlyPayment != null
        ? `quoted`
        : `saved_only`,
    useRefreshPricing: useCalculateBatteryProposalPricing,
    // The server's amount message names the bound and the price; generic save
    // copy would throw that away.
    describeSaveError: (error) =>
      batteryConcertAmountErrorMessage(error) ??
      (isPricingSupersededError(error)
        ? PRICING_SUPERSEDED_MESSAGE
        : SAVE_CONCERT_OFFER_ERROR_PROMPT),
    describeRefreshError: (error) =>
      isPricingSupersededError(error)
        ? PRICING_SUPERSEDED_MESSAGE
        : REFRESH_PRICING_ERROR_PROMPT,
    amountBlockedPrompt: concertAmountBlockedPrompt,
    copy: {
      saveLabel: SAVE_CONCERT_OFFER_LABEL,
      saveBusyLabel: SAVE_CONCERT_OFFER_BUSY_LABEL,
      savedLabel: SAVED_CONCERT_OFFER_LABEL,
      savedWithoutPaymentLabel: SAVED_CONCERT_OFFER_WITHOUT_PAYMENT_LABEL,
      pricingRequired: CONCERT_PRICING_REQUIRED_PROMPT,
      unavailable: CONCERT_UNAVAILABLE_PROMPT,
      empty: CONCERT_EMPTY_PROMPT,
      selectedOfferUnavailable: CONCERT_SELECTED_OFFER_UNAVAILABLE_PROMPT,
      amountIneligibleFallback: CONCERT_AMOUNT_INELIGIBLE_FALLBACK_PROMPT,
      readOnly: CONCERT_READ_ONLY_PROMPT,
    },
  }

/** Concert Battery's half of the Loan Product drawer. */
export const ConcertOfferPicker = ({
  proposalId,
  quote,
  onClose,
}: {
  proposalId: string
  quote: BatteryQuoteDto | null
  onClose: () => void
}): JSX.Element => (
  <SharedConcertOfferPicker
    bundle={batteryConcertOfferBundle}
    proposalId={proposalId}
    quote={quote}
    onClose={onClose}
  />
)
